import { FieldLabel } from "../workspaces/shared/FieldLabel";
import { useEffect, useState } from "react";
import { AppearancePicker } from "../appearance/AppearancePicker";
import { userMessage } from "../workspaces/shared/userMessage";
import { LEGAL_DOCUMENTS, type LegalDocument } from "../legal/documents";
import {
  fetchPricing,
  fetchPublicConfig,
  formatPrice,
  requestSignup,
  type PublicConfig,
  type PublicPricing,
} from "../session/session";
import { AuthLayout } from "../session/AuthLayout";
import { BrandMark, Callout } from "../workspaces/shared/ui";
import {
  ArrowRight,
  Calculator,
  Check,
  Compass,
  GraduationCap,
  Hammer,
  HardHat,
  KeyRound,
  Landmark,
  MailCheck,
  DraftingCompass,
  ReceiptText,
  Search,
  ShieldCheck,
  type LucideIcon,
} from "lucide-react";

/**
 * THE PUBLIC FRONT DOOR (spec §86-13).
 *
 * Just enough to say what Berrier Reno is, what it costs, and how to get in:
 * Home, Features, Pricing, Sign up, Sign in, Terms and Privacy. Part of the
 * same single-page application, so it ships with the app, obeys the same
 * strict Content-Security-Policy and loads no third-party script.
 *
 * EVERY CLAIM HERE IS SOMETHING THE APPLICATION DOES TODAY. It promises no
 * accuracy, no savings, no return and no approval: estimates are described
 * as planning tools because that is what they are.
 */

export type PublicPage = "home" | "features" | "pricing" | "signup" | "terms" | "privacy";

export function publicPageFor(path: string): PublicPage | null {
  const clean = path.replace(/\/+$/, "") || "/";
  switch (clean) {
    case "/":
      return "home";
    case "/features":
      return "features";
    case "/pricing":
      return "pricing";
    case "/signup":
      return "signup";
    case "/terms":
      return "terms";
    case "/privacy":
      return "privacy";
    default:
      return null;
  }
}

export function navigateTo(path: string): void {
  window.history.pushState({}, "", path);
  window.dispatchEvent(new PopStateEvent("popstate"));
  window.scrollTo(0, 0);
}

function Link({ to, children, className }: { to: string; children: React.ReactNode; className?: string }) {
  return (
    <a
      href={to}
      className={className}
      onClick={(event) => {
        if (event.metaKey || event.ctrlKey || event.shiftKey) return;
        event.preventDefault();
        navigateTo(to);
      }}
    >
      {children}
    </a>
  );
}

export function PublicLayout({ children, signupEnabled }: { children: React.ReactNode; signupEnabled: boolean }) {
  return (
    <div className="public-shell">
      <header className="public-header">
        <div className="public-header-inner">
          <Link to="/" className="public-brand">
            <BrandMark size={30} />
            <span>Berrier Reno</span>
            <span className="public-badge">Early Access</span>
          </Link>
          <nav className="public-nav" aria-label="Main">
            <AppearancePicker />
            <Link to="/features">Features</Link>
            <Link to="/pricing">Pricing</Link>
            <Link to="/login">Sign in</Link>
            {signupEnabled && (
              <Link to="/signup" className="btn btn-primary btn-small">
                Start Early Access
              </Link>
            )}
          </nav>
        </div>
      </header>
      <main className="public-main">{children}</main>
      <footer className="public-footer">
        <div className="public-footer-inner">
          <span className="public-footer-brand">
            <BrandMark size={22} /> Berrier Reno
          </span>
          <span>© {new Date().getFullYear()} Berrier Holdings LLC</span>
          <nav aria-label="Legal">
            <Link to="/features">Features</Link>
            <Link to="/pricing">Pricing</Link>
            <Link to="/terms">Terms</Link>
            <Link to="/privacy">Privacy</Link>
            <Link to="/login">Sign in</Link>
          </nav>
        </div>
      </footer>
    </div>
  );
}

/** The investor's lifecycle, in the order Berrier Reno follows it. Every step exists in the app today. */
const LIFECYCLE: { icon: LucideIcon; title: string; text: string }[] = [
  { icon: Compass, title: "Find", text: "Screen listings against your buy box" },
  { icon: Calculator, title: "Analyze", text: "Underwrite the deal, flip or rental" },
  { icon: KeyRound, title: "Buy", text: "Turn the deal into a project" },
  { icon: Hammer, title: "Renovate", text: "Scope, estimate, bid and schedule" },
  { icon: ReceiptText, title: "Track", text: "Every cost against the budget" },
  { icon: GraduationCap, title: "Learn", text: "Finished projects inform the next number" },
];

const CAPABILITIES: { icon: LucideIcon; title: string; lede: string; points: string[] }[] = [
  {
    icon: Search,
    title: "Before you buy",
    lede: "Know whether a house works before it becomes a project.",
    points: [
      "Deal analysis for flips and rentals — purchase, rehab, financing, profit, cash flow",
      "Buy boxes and comparable sales in Find (sample market during Early Access)",
      "A capital plan that shows whether projects can run at the same time",
    ],
  },
  {
    icon: DraftingCompass,
    title: "Plan the work",
    lede: "Describe the house once; the estimate follows.",
    points: [
      "Floor plans of the house as it is and as you plan it — or just room sizes",
      "Scope and estimate room by room, priced from quantities through materials and labor",
      "Whole-house finishes, packages for typical rooms, and a schedule built from the scope",
    ],
  },
  {
    icon: HardHat,
    title: "Hire and buy",
    lede: "Contractors price the same scope you estimated.",
    points: [
      "Bid requests by trade with blanks for material, labor and total — no estimate figures on them",
      "Record quotes, choose one, and see it replace the calculated figure",
      "A materials list of what to buy, what is bought and what was returned",
    ],
  },
  {
    icon: Landmark,
    title: "Run the money",
    lede: "Proposed, quoted and actually spent — side by side.",
    points: [
      "Receipts, purchases, expenses and mileage recorded against the project",
      "Reconciliation of what you estimated, what was quoted and what you spent",
      "Loans, draws and a cash forecast, with printable reports for lenders and accountants",
    ],
  },
];

/**
 * An illustration of the product, drawn with the product's own styles. The
 * figures are an example project and are labelled as one.
 */
function ProductPreview() {
  const bars = [
    { label: "Cabinetry", pct: 88, value: "$13,100" },
    { label: "Flooring", pct: 62, value: "$9,200" },
    { label: "Plumbing", pct: 45, value: "$6,700" },
    { label: "Electrical", pct: 31, value: "$4,600" },
  ];
  return (
    <figure className="public-preview" aria-label="Illustration of a Berrier Reno project overview">
      <div className="public-preview-window">
        <div className="public-preview-bar">
          <span />
          <span />
          <span />
        </div>
        <div className="public-preview-body">
          <div className="public-preview-head">
            <span className="job-chip">26014</span>
            <strong>418 Maple Avenue</strong>
            <span className="badge badge-good">Active</span>
          </div>
          <div className="public-preview-metrics">
            <div>
              <span>Purchase</span>
              <strong>$84,900</strong>
            </div>
            <div className="is-emphasis">
              <span>Working estimate</span>
              <strong>$42,300</strong>
            </div>
            <div>
              <span>Expected ARV</span>
              <strong>$215,000</strong>
            </div>
          </div>
          <div className="public-preview-chart">
            <span className="public-preview-caption">Where the estimate goes</span>
            {bars.map((bar) => (
              <div key={bar.label} className="public-preview-row">
                <span>{bar.label}</span>
                <span className="public-preview-track">
                  <span style={{ width: `${bar.pct}%` }} />
                </span>
                <span>{bar.value}</span>
              </div>
            ))}
          </div>
          <div className="public-preview-compare">
            <div>
              <span>Proposed</span>
              <strong>$42,300</strong>
            </div>
            <ArrowRight size={14} aria-hidden="true" />
            <div>
              <span>Quoted</span>
              <strong>$44,150</strong>
            </div>
            <ArrowRight size={14} aria-hidden="true" />
            <div>
              <span>Spent so far</span>
              <strong>$18,420</strong>
            </div>
          </div>
        </div>
      </div>
      <figcaption>Illustration — an example project, not real figures.</figcaption>
    </figure>
  );
}

function PrimaryCta({ signupEnabled }: { signupEnabled: boolean }) {
  return signupEnabled ? (
    <Link to="/signup" className="btn btn-primary btn-large">
      Start Early Access <ArrowRight size={18} aria-hidden="true" />
    </Link>
  ) : (
    <Link to="/pricing" className="btn btn-primary btn-large">
      See Early Access <ArrowRight size={18} aria-hidden="true" />
    </Link>
  );
}

function Home({ signupEnabled }: { signupEnabled: boolean }) {
  return (
    <>
      <section className="public-hero">
        <div className="public-hero-copy">
          <span className="public-kicker">For renovation investors and renovators</span>
          <h1>From the first look at a house to the last receipt.</h1>
          <p className="public-lede">
            Berrier Reno analyzes the deal, plans and prices the renovation, sends it out for bids and tracks every dollar
            against the budget — in one place, with every figure showing where it came from.
          </p>
          <div className="public-cta">
            <PrimaryCta signupEnabled={signupEnabled} />
            <Link to="/login" className="btn btn-large">
              Sign in
            </Link>
          </div>
          <p className="public-reassure">
            <ShieldCheck size={16} aria-hidden="true" /> Your projects stay private to your account.
          </p>
        </div>
        <ProductPreview />
      </section>

      <section className="public-band">
        <div className="public-band-inner">
          <h2 className="public-section-title">One place for the whole project</h2>
          <ol className="public-lifecycle">
            {LIFECYCLE.map(({ icon: Icon, title, text }) => (
              <li key={title}>
                <span className="public-lifecycle-icon" aria-hidden="true">
                  <Icon size={20} />
                </span>
                <strong>{title}</strong>
                <span>{text}</span>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section className="public-section">
        <h2 className="public-section-title">What it does</h2>
        <div className="public-capabilities">
          {CAPABILITIES.map(({ icon: Icon, title, lede, points }) => (
            <article key={title} className="public-capability">
              <span className="public-capability-icon" aria-hidden="true">
                <Icon size={22} />
              </span>
              <h3>{title}</h3>
              <p className="public-capability-lede">{lede}</p>
              <ul>
                {points.map((point) => (
                  <li key={point}>
                    <Check size={16} aria-hidden="true" />
                    <span>{point}</span>
                  </li>
                ))}
              </ul>
            </article>
          ))}
        </div>
      </section>

      <section className="public-section public-steps-section">
        <h2 className="public-section-title">How a project comes together</h2>
        <ol className="public-steps">
          <li>
            <span className="public-step-number">1</span>
            <strong>Describe the house and the work</strong>
            <p>Draw the plan or enter room sizes, then choose the work room by room.</p>
          </li>
          <li>
            <span className="public-step-number">2</span>
            <strong>Get a budget you can defend</strong>
            <p>Every item is priced from its quantity and rolled up by trade. Anything unpriced is marked, never counted as free.</p>
          </li>
          <li>
            <span className="public-step-number">3</span>
            <strong>Bid it, build it, track it</strong>
            <p>Send bid requests, choose quotes, record costs and watch proposed, quoted and actual side by side.</p>
          </li>
        </ol>
      </section>

      <section className="public-section">
        <div className="public-cta-band">
          <div>
            <h2>Try it on your next house.</h2>
            <p>
              Early Access is growing quickly. Estimates are planning tools, not quotes, and Find uses a sample market until live
              listing data is connected.
            </p>
          </div>
          <PrimaryCta signupEnabled={signupEnabled} />
        </div>
      </section>
    </>
  );
}

function Features({ signupEnabled }: { signupEnabled: boolean }) {
  return (
    <>
      <section className="public-section public-page-head">
        <span className="public-kicker">Features</span>
        <h1>Everything a renovation project needs, in the order it happens.</h1>
        <p className="public-lede">From the first walkthrough to the last receipt, in one project.</p>
      </section>
      <section className="public-section">
        <ol className="public-lifecycle public-lifecycle-light">
          {LIFECYCLE.map(({ icon: Icon, title, text }) => (
            <li key={title}>
              <span className="public-lifecycle-icon" aria-hidden="true">
                <Icon size={20} />
              </span>
              <strong>{title}</strong>
              <span>{text}</span>
            </li>
          ))}
        </ol>
      </section>
      <section className="public-section">
        <div className="public-capabilities">
          {CAPABILITIES.map(({ icon: Icon, title, lede, points }) => (
            <article key={title} className="public-capability">
              <span className="public-capability-icon" aria-hidden="true">
                <Icon size={22} />
              </span>
              <h3>{title}</h3>
              <p className="public-capability-lede">{lede}</p>
              <ul>
                {points.map((point) => (
                  <li key={point}>
                    <Check size={16} aria-hidden="true" />
                    <span>{point}</span>
                  </li>
                ))}
              </ul>
            </article>
          ))}
        </div>
      </section>
      <section className="public-section">
        <div className="public-cta-band">
          <div>
            <h2>See it with your own numbers.</h2>
            <p>
              Berrier Reno does not replace a contractor, inspector, appraiser, engineer, lender or tax professional, and it does not
              guarantee any cost, value, rent or profit.
            </p>
          </div>
          <PrimaryCta signupEnabled={signupEnabled} />
        </div>
      </section>
    </>
  );
}

function Pricing({ config }: { config: PublicConfig | null }) {
  const [pricing, setPricing] = useState<PublicPricing | null>(null);
  useEffect(() => {
    void fetchPricing().then(setPricing);
  }, []);
  const price = pricing ? formatPrice(pricing) : null;
  return (
    <>
      <section className="public-section public-page-head public-page-head-center">
        <span className="public-kicker">Pricing</span>
        <h1>One plan. Everything in Berrier Reno.</h1>
        <p className="public-lede">Early Access includes every feature today, and what we add while it grows.</p>
      </section>
      <section className="public-section">
        <div className="public-price-card">
          <div className="public-price-head">
            <span className="badge badge-brand">Early Access</span>
            <h2>{pricing?.productName ?? "Berrier Reno Early Access"}</h2>
            <p className="public-price">{price ?? "Price announced soon"}</p>
          </div>
          <ul>
            {[
              "Unlimited projects and deals",
              "Floor plans, scope, estimates and renovation budgets",
              "Contractor bid requests and quote comparison",
              "Materials, costs, receipts, financing and cash forecasts",
              "Printable reports and spreadsheet exports",
              "A direct line to the people building it",
            ].map((item) => (
              <li key={item}>
                <Check size={16} aria-hidden="true" />
                <span>{item}</span>
              </li>
            ))}
          </ul>
          {config?.signupEnabled ? (
            <Link to="/signup" className="btn btn-primary btn-large">
              Start Early Access
            </Link>
          ) : (
            <p className="public-price-note">Early Access is by invitation at the moment.</p>
          )}
          <p className="public-price-note">
            Billed through Stripe. Cancel any time from your account; you keep access until the end of the period you paid for, and your
            projects are kept.
          </p>
        </div>
      </section>
    </>
  );
}

function Signup({ config }: { config: PublicConfig | null }) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [accept, setAccept] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState<string | null>(null);

  if (config && !config.signupEnabled) {
    return (
      <AuthLayout title="Early Access is by invitation" lede="Sign-up is not open right now. If you have an invitation, use the link in that email.">
        <Link to="/login" className="btn btn-primary">
          Sign in
        </Link>
      </AuthLayout>
    );
  }

  if (sent) {
    return (
      <AuthLayout title="Check your email">
        <div className="auth-success">
          <span className="auth-success-icon" aria-hidden="true">
            <MailCheck size={26} />
          </span>
          <p>{sent}</p>
          <p className="auth-hint">The link works once and expires in 7 days. Nothing arrived? Check spam, or sign up again to send a fresh link.</p>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Start Early Access"
      lede="We will email you a link to confirm your address and choose a password."
      footer={
        <>
          Already have an account? <Link to="/login">Sign in</Link>
        </>
      }
    >
      <form
        className="auth-form"
        onSubmit={(event) => {
          event.preventDefault();
          setBusy(true);
          setError(null);
          void requestSignup(email, name, accept)
            .then((message) => setSent(message))
            .catch((err: unknown) => setError(userMessage(err, "That did not work.")))
            .finally(() => setBusy(false));
        }}
      >
        <FieldLabel>
          Your name
          <input value={name} onChange={(e) => setName(e.target.value)} autoComplete="name" required autoFocus />
        </FieldLabel>
        <FieldLabel>
          Email
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" required />
        </FieldLabel>
        <FieldLabel className="checkbox-label">
          <input type="checkbox" checked={accept} onChange={(e) => setAccept(e.target.checked)} required />
          <span>
            I agree to the <Link to="/terms">Terms of Service</Link> and <Link to="/privacy">Privacy Policy</Link>, and I understand Berrier
            Reno&rsquo;s estimates are planning tools, not quotes.
          </span>
        </FieldLabel>
        {error && <Callout tone="bad">{error}</Callout>}
        <button className="btn btn-primary" type="submit" disabled={busy || !accept}>
          {busy ? "Sending…" : "Email me a link"}
        </button>
      </form>
    </AuthLayout>
  );
}

export function LegalPage({ document }: { document: LegalDocument }) {
  return (
    <article className="public-section legal-document">
      <span className="public-kicker">Legal</span>
      <h1>{document.title}</h1>
      <p className="muted">
        Version {document.version} · Effective {document.effectiveDate}
      </p>
      <p className="legal-summary">{document.summary}</p>
      {document.sections.map((section) => (
        <section key={section.heading}>
          <h2>{section.heading}</h2>
          {section.paragraphs.map((paragraph, index) => (
            <p key={index}>{paragraph}</p>
          ))}
        </section>
      ))}
    </article>
  );
}

export function PublicSite({ page }: { page: PublicPage }) {
  const [config, setConfig] = useState<PublicConfig | null>(null);
  useEffect(() => {
    void fetchPublicConfig().then(setConfig);
  }, []);
  useEffect(() => {
    const titles: Record<PublicPage, string> = {
      home: "Berrier Reno — renovation investing from the first look to the last receipt",
      features: "Features — Berrier Reno",
      pricing: "Pricing — Berrier Reno",
      signup: "Start Early Access — Berrier Reno",
      terms: "Terms of Service — Berrier Reno",
      privacy: "Privacy Policy — Berrier Reno",
    };
    document.title = titles[page];
  }, [page]);
  const signupEnabled = config?.signupEnabled ?? false;
  if (page === "signup") return <Signup config={config} />;
  return (
    <PublicLayout signupEnabled={signupEnabled}>
      {page === "home" ? (
        <Home signupEnabled={signupEnabled} />
      ) : page === "features" ? (
        <Features signupEnabled={signupEnabled} />
      ) : page === "pricing" ? (
        <Pricing config={config} />
      ) : (
        <LegalPage document={LEGAL_DOCUMENTS[page]} />
      )}
    </PublicLayout>
  );
}
